<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Attendance Session
                </h1>
                <p
                    class="text-sm text-gray-500 dark:text-gray-400 mt-1"
                    v-if="attendance"
                >
                    {{ classSubjectLabel }} ·
                    {{ formatDate(attendance.attendance_date) }}
                </p>
            </div>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
            ></div>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <template v-if="!loading && attendance">
            <!-- Session Info -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <!-- Session Details -->
                <div
                    class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:col-span-2 space-y-4"
                >
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                            >
                                Mode
                            </p>
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ attendance.mode_text }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                            >
                                Time
                            </p>
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ formatTime(attendance.start_time) }} –
                                {{ formatTime(attendance.end_time) }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                            >
                                Teacher
                            </p>
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ attendance.teacher?.user?.name || "-" }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                            >
                                Status
                            </p>
                            <span
                                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                :class="
                                    attendance.is_locked
                                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                        : 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                "
                            >
                                {{ attendance.is_locked ? "Locked" : "Open" }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="attendance.notes"
                        class="text-sm text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 pt-3"
                    >
                        {{ attendance.notes }}
                    </div>

                    <div class="flex flex-wrap gap-3 pt-2">
                        <button
                            v-if="attendance.mode === 'qr'"
                            @click="regenerateQr"
                            :disabled="actionLoading"
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                        >
                            <RefreshCw class="w-4 h-4" />
                            Regenerate QR
                        </button>
                        <button
                            @click="toggleLock"
                            :disabled="actionLoading"
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
                            :class="
                                attendance.is_locked
                                    ? 'bg-success-600 text-white hover:bg-success-700'
                                    : 'bg-warning-600 text-white hover:bg-warning-700'
                            "
                        >
                            <Lock
                                v-if="!attendance.is_locked"
                                class="w-4 h-4"
                            />
                            <LockOpen v-else class="w-4 h-4" />
                            {{
                                attendance.is_locked
                                    ? "Unlock Session"
                                    : "Lock Session"
                            }}
                        </button>
                        <button
                            @click="markUnmarkedPresent"
                            :disabled="
                                actionLoading ||
                                attendance.is_locked ||
                                unmarkedCount === 0
                            "
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                        >
                            <CheckCheck class="w-4 h-4" />
                            Mark Unmarked Present ({{ unmarkedCount }})
                        </button>
                    </div>
                </div>

                <!-- QR Code Card -->
                <div
                    v-if="attendance.mode === 'qr'"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex flex-col items-center"
                >
                    <p
                        class="text-sm font-medium text-gray-900 dark:text-white mb-3"
                    >
                        Session Code
                    </p>
                    <canvas ref="qrCanvas" class="mb-3"></canvas>
                    <p
                        class="text-2xl font-mono font-bold tracking-widest text-gray-900 dark:text-white mb-2"
                    >
                        {{ attendance.qr_code }}
                    </p>
                    <p
                        class="text-xs text-gray-500 dark:text-gray-400 text-center"
                    >
                        Students scan this QR (phone camera) or type the code on
                        <span class="font-medium">My Attendance</span>.
                        <br />
                        Expired: {{ formatDateTime(attendance.qr_expires_at) }}
                    </p>
                </div>
            </div>

            <!-- Roster Table -->
            <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2
                        class="text-sm font-semibold text-gray-900 dark:text-white"
                    >
                        Student Roster ({{ roster.length }})
                    </h2>
                </div>

                <div v-if="rosterLoading" class="p-4">
                    <SkeletonLoader variant="table" :rows="5" :cols="5" />
                </div>

                <div v-else-if="roster.length === 0" class="p-4">
                    <EmptyState
                        icon="UsersIcon"
                        title="No enrolled students"
                        description="Enroll students in this class to track their attendance."
                    />
                </div>

                <div v-else class="overflow-x-auto">
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Student
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    NISN
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Check-in
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="row in roster"
                                :key="row.student.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ row.student.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ row.student.nisn || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        row.log?.check_in_time
                                            ? formatDateTime(
                                                  row.log.check_in_time,
                                              )
                                            : "-"
                                    }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="row.log"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="statusClass(row.log.status)"
                                    >
                                        {{ statusLabel(row.log.status) }}
                                    </span>
                                    <span v-else class="text-xs text-gray-400">
                                        Not marked
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <template v-if="!attendance.is_locked">
                                        <button
                                            v-for="action in statusActions"
                                            :key="action.value"
                                            @click="
                                                changeStatus(row, action.value)
                                            "
                                            :title="action.title"
                                            class="inline-flex items-center justify-center p-2 rounded-md transition-colors mr-1"
                                            :class="
                                                row.log?.status === action.value
                                                    ? action.class +
                                                      ' ring-1 ring-current'
                                                    : action.class
                                            "
                                        >
                                            <component
                                                :is="action.icon"
                                                class="w-4 h-4"
                                            />
                                        </button>
                                    </template>
                                    <span v-else class="text-xs text-gray-400">
                                        Locked
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import QRCode from "qrcode";
import {
    RefreshCw,
    Lock,
    LockOpen,
    CheckCheck,
    CheckCircle2,
    XCircle,
    Clock,
    Info,
} from "@lucide/vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import attendanceApi, { Attendance, RosterRow } from "@/api/attendance";

type AttendanceStatus = "present" | "absent" | "late" | "excused";

const route = useRoute();
const router = useRouter();

const attendanceId = route.params.id as string;

const attendance = ref<Attendance | null>(null);
const roster = ref<RosterRow[]>([]);
const loading = ref(false);
const rosterLoading = ref(false);
const actionLoading = ref(false);
const error = ref<string | null>(null);

const qrCanvas = ref<HTMLCanvasElement | null>(null);

const statusActions: {
    value: AttendanceStatus;
    title: string;
    icon: any;
    class: string;
}[] = [
    {
        value: "present",
        title: "Mark Present",
        icon: CheckCircle2,
        class: "text-success-600 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20",
    },
    {
        value: "absent",
        title: "Mark Absent",
        icon: XCircle,
        class: "text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20",
    },
    {
        value: "late",
        title: "Mark Late",
        icon: Clock,
        class: "text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20",
    },
    {
        value: "excused",
        title: "Mark Excused",
        icon: Info,
        class: "text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
    },
];

const classSubjectLabel = computed(() => {
    if (!attendance.value?.class_subject) return "";
    const cs = attendance.value.class_subject;
    return `${cs.class?.grade_level || ""} ${cs.class?.name || ""} - ${cs.subject?.name || ""}`;
});

const unmarkedCount = computed(
    () => roster.value.filter((row) => !row.log).length,
);

async function fetchAttendance() {
    loading.value = true;
    error.value = null;

    try {
        attendance.value = await attendanceApi.getById(attendanceId);
        await nextTick();
        renderQr();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch attendance";
    } finally {
        loading.value = false;
    }
}

async function fetchRoster() {
    rosterLoading.value = true;

    try {
        roster.value = await attendanceApi.getRoster(attendanceId);
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch student roster";
    } finally {
        rosterLoading.value = false;
    }
}

function renderQr() {
    if (!qrCanvas.value || !attendance.value?.qr_code) return;

    const checkInUrl = `${window.location.origin}/student-attendance?code=${attendance.value.qr_code}`;

    QRCode.toCanvas(qrCanvas.value, checkInUrl, {
        width: 160,
        margin: 1,
    });
}

async function regenerateQr() {
    actionLoading.value = true;

    try {
        attendance.value = await attendanceApi.regenerateQr(attendanceId);
        await nextTick();
        renderQr();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to regenerate QR code";
    } finally {
        actionLoading.value = false;
    }
}

async function toggleLock() {
    actionLoading.value = true;

    try {
        attendance.value = await attendanceApi.toggleLock(attendanceId);
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to toggle session lock";
    } finally {
        actionLoading.value = false;
    }
}

async function markUnmarkedPresent() {
    actionLoading.value = true;
    error.value = null;

    try {
        const entries = roster.value
            .filter((row) => !row.log)
            .map((row) => ({
                student_id: row.student.id,
                status: "present" as const,
            }));

        if (entries.length > 0) {
            await attendanceApi.bulkMarkLogs(attendanceId, entries);
        }

        await fetchRoster();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to mark students present";
    } finally {
        actionLoading.value = false;
    }
}

async function changeStatus(row: RosterRow, status: AttendanceStatus) {
    error.value = null;

    try {
        if (row.log) {
            await attendanceApi.updateLog(row.log.id, { status });
        } else {
            await attendanceApi.markLog(attendanceId, {
                student_id: row.student.id,
                status,
            });
        }

        await fetchRoster();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to update attendance";
    }
}

function statusLabel(status: string) {
    const labels: Record<string, string> = {
        present: "Present",
        absent: "Absent",
        late: "Late",
        excused: "Excused",
    };
    return labels[status] ?? status;
}

function statusClass(status: string) {
    const classes: Record<string, string> = {
        present:
            "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200",
        absent: "bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200",
        late: "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200",
        excused:
            "bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
}

function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function formatTime(dateString: string) {
    return new Date(dateString).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDateTime(dateString?: string | null) {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function goBack() {
    router.push("/attendance");
}

function clearError() {
    error.value = null;
}

onMounted(() => {
    fetchAttendance();
    fetchRoster();
});
</script>
